/**
 * QuoteDiscountsAdmin.tsx — Admin > Quote Discounts (behind the Quotes module).
 *
 * The discounts a quote can apply, laid out like every Admin list (Lead
 * Sources is the reference — components/admin/AdminList.tsx; Payment
 * Discounts is the pattern for deleted rows). A Prospera-only section: no
 * corp-dashboard page, so no corp filters or columns. It keeps its own
 * columns (Name, Type, Amount) and the server's order until a column title
 * is clicked to sort by it.
 *   - "Add Discount" top right; search by name or description and Show
 *     deleted in the toolbar;
 *   - an inactive discount is a deleted one (delete sets active = 0 on the
 *     server): hidden until Show deleted, then amber, struck through with a
 *     "Deleted" badge, Restore only, and not opened by a click;
 *   - clicking a live row opens its edit form (no Active box — delete and
 *     restore are what turn a discount off and on; a new one starts active);
 *     Edit / Delete icons on a live row, Restore on a deleted one; delete
 *     asks first;
 *   - a failed load, save or delete shows the server's reason; a restore
 *     toasts its outcome.
 *
 * Phones keep the treatment the page had: the wrapper around AdminListPage
 * releases the viewport lock and clears the browser chrome.
 *
 * On a Locked page (useAdminPageLevel) there is no Add, no row icon and no
 * Actions column; a live row still opens, as a viewer with a single Close.
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Badge } from '@/components/ui/Badge';
import { FormField } from '@/components/ui/FormField';
import { SpinnerOverlay } from '@/components/ui/Spinner';
import {
  ACTIONS_COLUMN,
  ADMIN_TD_CLASS,
  AdminListEmpty,
  AdminListPage,
  AdminListTable,
  AdminListToolbar,
  AdminRowName,
  PaginationFooter,
  adminRowClass,
  type AdminListColumn,
} from '@/components/admin/AdminList';
import { AdminRowAction, AdminRowActions } from '@/components/admin/AdminRowActions';
import { AdminLockedFieldset } from '@/components/admin/AdminLockedFieldset';
import { useAdminPageLevel } from '@/components/admin/AdminPageLevelContext';
import { useListPagination } from '@/components/admin/useListPagination';
import { compareNumber, compareText, useListSort, type ListSortCompare } from '@/components/admin/useListSort';
import * as adminSvc from '@/services/admin.service';
import type { Discount, DiscountScope } from '@/services/admin.service';
import { getErrorMessage } from '@/services/apiClient';
import { toast } from '@/store/toastStore';
import { formatCurrency } from '@/lib/utils';
import {
  DISCOUNT_SCOPE_OPTIONS,
  DISCOUNT_TYPE_OPTIONS,
  DEFAULT_DISCOUNT_IS_PERCENT,
  DEFAULT_DISCOUNT_SCOPE,
  discountTypeLabel,
} from '@/pages/quotes/discountOptions';

// ─── Form state type ──────────────────────────────────────────────────────────

interface DiscountForm {
  name: string;
  description: string;
  discountType: 'percentage' | 'fixed';
  quoteElement: DiscountScope;
  amount: string;
}

const EMPTY_FORM: DiscountForm = {
  name:         '',
  description:  '',
  discountType: DEFAULT_DISCOUNT_IS_PERCENT ? 'percentage' : 'fixed',
  quoteElement: DEFAULT_DISCOUNT_SCOPE,
  amount:       '',
};

/** The admin API speaks 'percentage' | 'fixed' where the quote speaks isPercent. */
const TYPE_SELECT_OPTIONS = DISCOUNT_TYPE_OPTIONS.map((o) => ({
  value: o.isPercent ? 'percentage' : 'fixed',
  label: o.label,
}));

const APPLIES_TO_OPTIONS = DISCOUNT_SCOPE_OPTIONS.map((o) => ({ value: o.value, label: o.label }));

// Shown only when the server sends no message of its own.
const LOAD_FAILED =
  'Discounts could not be loaded. Refresh the page to try again. If it keeps happening, contact Prospera support.';
const SAVE_FAILED =
  'The discount could not be saved. Check the fields and try again. If it keeps happening, contact Prospera support.';
const DELETE_FAILED =
  'The discount could not be deleted. Try again. If it keeps happening, contact Prospera support.';
const RESTORE_FAILED =
  'The discount could not be restored. Try again. If it keeps happening, contact Prospera support.';

const COLUMNS: AdminListColumn[] = [
  { key: 'name', header: 'Name', sortable: true },
  { key: 'discountType', header: 'Type', className: 'w-[15%]', sortable: true },
  { key: 'amount', header: 'Amount', className: 'w-[15%]', sortable: true },
  ACTIONS_COLUMN,
];

/** A Locked page has no row actions, so no Actions column. */
const LOCKED_COLUMNS = COLUMNS.filter((c) => c !== ACTIONS_COLUMN);

/** No column is sorted to start: the list opens in the server's order. */
const SORT_COMPARE: ListSortCompare<Discount> = {
  name: (a, b) => compareText(a.name, b.name),
  discountType: (a, b) =>
    compareText(discountTypeLabel(a.discountType === 'percentage'), discountTypeLabel(b.discountType === 'percentage')),
  amount: (a, b) => compareNumber(a.amount, b.amount),
};

/** Delete sets active = 0 on the server, so an inactive discount is a deleted one. */
function isDeleted(d: Discount): boolean {
  return !d.active;
}

// ─── Modal ────────────────────────────────────────────────────────────────────

interface DiscountModalProps {
  discount?: Discount | null;
  onClose: () => void;
  onSaved: () => void;
}

function DiscountModal({ discount, onClose, onSaved }: DiscountModalProps) {
  const isEdit = discount != null;
  // Locked: a viewer — fields read-only, Close only.
  const locked = useAdminPageLevel() === 'locked';

  const [form, setForm] = useState<DiscountForm>(() =>
    isEdit
      ? {
          name:         discount.name,
          description:  discount.description ?? '',
          discountType: discount.discountType,
          quoteElement: discount.quoteElement ?? 'all',
          amount:       String(discount.amount),
        }
      : EMPTY_FORM,
  );

  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState<string | null>(null);

  function set<K extends keyof DiscountForm>(field: K, value: DiscountForm[K]) {
    setForm((f) => ({ ...f, [field]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim() || locked) return;

    const payload = {
      name:         form.name.trim(),
      description:  form.description,
      discountType: form.discountType,
      quoteElement: form.quoteElement,
      amount:       parseFloat(form.amount) || 0,
    };

    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        // No `active`: the update is partial, so an edit never deletes or restores.
        await adminSvc.updateDiscount(discount.discountId, payload);
      } else {
        await adminSvc.createDiscount({ ...payload, active: true });
      }
      onSaved();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err, SAVE_FAILED));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={locked ? 'Discount' : isEdit ? 'Edit Discount' : 'Add Discount'}
      size="md"
      footer={
        locked ? (
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving || !form.name.trim()}
              loading={saving}
            >
              {isEdit ? 'Save Changes' : 'Add Discount'}
            </Button>
          </>
        )
      }
    >
      <AdminLockedFieldset>
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}

          <FormField label="Name" required={!locked}>
            <Input
              value={form.name}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Senior Discount"
              required
              autoFocus
            />
          </FormField>

          <FormField label="Description">
            <Input
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder="Optional — shown with the discount on a quote"
            />
          </FormField>

          {/* The type and its amount side by side; the amount shows the type's unit. */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <FormField label="Discount Type">
              <Select
                options={TYPE_SELECT_OPTIONS}
                value={form.discountType}
                onChange={(val) => set('discountType', (val as 'percentage' | 'fixed') ?? EMPTY_FORM.discountType)}
              />
            </FormField>

            <FormField label="Amount">
              <Input
                type="number"
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                leftAddon={form.discountType === 'percentage' ? '%' : '$'}
                placeholder={form.discountType === 'percentage' ? 'e.g. 10' : 'e.g. 25.00'}
              />
            </FormField>
          </div>

          <FormField label="Applies To">
            <Select
              options={APPLIES_TO_OPTIONS}
              value={form.quoteElement}
              onChange={(val) => set('quoteElement', (val as DiscountScope) ?? DEFAULT_DISCOUNT_SCOPE)}
            />
          </FormField>
        </form>
      </AdminLockedFieldset>
    </Modal>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function QuoteDiscountsAdmin() {
  const locked = useAdminPageLevel() === 'locked';
  const [discounts, setDiscounts]     = useState<Discount[]>([]);
  const [loading, setLoading]         = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [showCreate, setShowCreate]   = useState(false);
  const [editItem, setEditItem]       = useState<Discount | null>(null);
  const [deleteItem, setDeleteItem]   = useState<Discount | null>(null);
  const [loadError, setLoadError]     = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminSvc.listDiscounts();
      setDiscounts(data);
      setLoadError(null);
    } catch (err) {
      setLoadError(getErrorMessage(err, LOAD_FAILED));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  function setActive(id: number, active: boolean) {
    setDiscounts((prev) => prev.map((d) => (d.discountId === id ? { ...d, active } : d)));
  }

  async function handleDelete() {
    if (!deleteItem) return;
    const id = deleteItem.discountId;
    setDeleteError(null);
    try {
      await adminSvc.deleteDiscount(id);
    } catch (err) {
      setDeleteError(getErrorMessage(err, DELETE_FAILED));
      return;
    } finally {
      setDeleteItem(null);
    }
    // The server keeps the row, inactive: it becomes a deleted row.
    setActive(id, false);
  }

  async function handleRestore(item: Discount) {
    try {
      // The server validates `name` and `amount` as required even on a
      // partial update, so their unchanged values ride along with `active`.
      await adminSvc.updateDiscount(item.discountId, { name: item.name, amount: item.amount, active: true });
      setActive(item.discountId, true);
      toast.success('Discount restored');
    } catch (err) {
      toast.error(getErrorMessage(err, RESTORE_FAILED));
    }
  }

  const rows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return discounts
      .filter((d) => showDeleted || !isDeleted(d))
      .filter(
        (d) => !q || d.name.toLowerCase().includes(q) || (d.description ?? '').toLowerCase().includes(q),
      );
  }, [discounts, searchQuery, showDeleted]);

  const sorting = useListSort(rows, { compare: SORT_COMPARE });
  const pagination = useListPagination(sorting.sorted, { resetOn: [searchQuery, showDeleted] });

  return (
    <div className="h-full max-md:h-auto max-md:overflow-visible max-md:pb-16">
      <AdminListPage
        title="Quote Discounts"
        subtitle="Manage discounts that can be applied to patient quotes."
        actions={
          <Button onClick={() => setShowCreate(true)} leftIcon={<Plus className="h-4 w-4" />}>
            Add Discount
          </Button>
        }
        toolbar={
          <AdminListToolbar
            search={searchQuery}
            onSearchChange={setSearchQuery}
            searchPlaceholder="Search discounts…"
            searchLabel="Search quote discounts"
            showDeleted={showDeleted}
            onShowDeletedChange={setShowDeleted}
            showDeletedId="quote-discounts-show-deleted"
          />
        }
      >
        {deleteError && (
          <p role="alert" className="flex-shrink-0 mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {deleteError}
          </p>
        )}

        {loadError ? (
          <p role="alert" className="flex-shrink-0 text-sm text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">
            {loadError}
          </p>
        ) : loading ? (
          <SpinnerOverlay label="Loading discounts…" />
        ) : rows.length === 0 ? (
          <AdminListEmpty
            title="No discounts found"
            hint={searchQuery.trim() ? 'Try adjusting your search.' : locked ? undefined : 'Add your first discount to get started.'}
          />
        ) : (
          <>
            <AdminListTable columns={locked ? LOCKED_COLUMNS : COLUMNS} aria-label="Quote discounts" sort={sorting.sort} onSortChange={sorting.onSortChange}>
              {pagination.pageItems.map((row) => {
                const deleted = isDeleted(row);
                return (
                  <tr
                    key={row.discountId}
                    className={adminRowClass({ deleted, clickable: !deleted })}
                    // A live row opens its form; a deleted one is restored, not edited.
                    onClick={deleted ? undefined : () => setEditItem(row)}
                  >
                    <td className={ADMIN_TD_CLASS}>
                      <AdminRowName deleted={deleted} nameTestId="quote-discount-name">{row.name}</AdminRowName>
                    </td>
                    <td className={ADMIN_TD_CLASS}>
                      <Badge variant={row.discountType === 'percentage' ? 'info' : 'warning'}>
                        {discountTypeLabel(row.discountType === 'percentage')}
                      </Badge>
                    </td>
                    <td className={ADMIN_TD_CLASS}>
                      <span className={`text-sm ${deleted ? 'text-slate-400' : 'text-slate-700'}`}>
                        {row.discountType === 'percentage'
                          ? `${row.amount}%`
                          : formatCurrency(row.amount)}
                      </span>
                    </td>
                    {!locked && (
                      <td className={ADMIN_TD_CLASS}>
                        <AdminRowActions>
                          {deleted ? (
                            <AdminRowAction
                              kind="restore"
                              label={`Restore ${row.name}`}
                              onClick={() => void handleRestore(row)}
                            />
                          ) : (
                            <>
                              <AdminRowAction kind="edit" label={`Edit ${row.name}`} onClick={() => setEditItem(row)} />
                              <AdminRowAction kind="delete" label={`Delete ${row.name}`} onClick={() => setDeleteItem(row)} />
                            </>
                          )}
                        </AdminRowActions>
                      </td>
                    )}
                  </tr>
                );
              })}
            </AdminListTable>
            <PaginationFooter pagination={pagination} noun={['discount', 'discounts']} />
          </>
        )}

        {showCreate && (
          <DiscountModal onClose={() => setShowCreate(false)} onSaved={load} />
        )}

        {editItem && (
          <DiscountModal
            discount={editItem}
            onClose={() => setEditItem(null)}
            onSaved={load}
          />
        )}

        <ConfirmDialog
          isOpen={deleteItem !== null}
          title="Delete Discount"
          message={
            deleteItem
              ? `Are you sure you want to delete "${deleteItem.name}"?`
              : ''
          }
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDelete}
          onClose={() => setDeleteItem(null)}
        />
      </AdminListPage>
    </div>
  );
}
